import { AnnouncementInput } from '@kanos/domain';
import { Button, DateField, SelectField, StatusBadge, TextField } from '@kanos/ui';
import {
  AddButton,
  Card,
  checkForm,
  fieldRule,
  formatDateTime,
  FormDialog,
  QueryContent,
  todayIso,
} from '@kanos/web-core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { DeleteEntryDialog, useEntryForm } from './entry-dialogs';
import { dayAndHourOf, HOURS, isoOf } from './hours';
import {
  announcementsQuery,
  useDeleteAnnouncement,
  useSaveAnnouncement,
  type Announcement,
} from './queries';

const shape = AnnouncementInput.shape;

/** Writing an announcement, or changing one: `entry` is the one changed, `null` a new one. */
function AnnouncementDialog({
  entry,
  onClose,
}: {
  entry: Announcement | null;
  onClose: () => void;
}) {
  const save = useSaveAnnouncement();
  const [title, setTitle] = useState(entry?.title ?? '');
  const [body, setBody] = useState(entry?.body ?? '');
  const [at, setAt] = useState(
    entry
      ? dayAndHourOf(entry.publishedAt)
      : { day: todayIso(), hour: String(new Date().getHours()) },
  );
  const form = useEntryForm(save, entry?.id ?? null, onClose);

  const submit = () => {
    const checked = checkForm(AnnouncementInput, {
      title,
      body,
      publishedAt: isoOf(at.day, at.hour),
    });
    form.submit(checked);
  };

  return (
    <FormDialog
      title={entry ? 'Neuigkeit ändern' : 'Neuigkeit schreiben'}
      description="Erscheint in der Kanzlei-App unter Hilfe › Neuigkeiten, ab der Veröffentlichung. Eine leere Zeile beginnt einen neuen Absatz."
      submitLabel={entry ? 'Speichern' : 'Schreiben'}
      {...form.dialog}
      onSubmit={submit}
      draft={{ title, body, at }}
      onClose={onClose}
    >
      <TextField
        label="Titel"
        placeholder="z. B. Neu: Logo der Kanzlei im Portal"
        value={title}
        onChange={setTitle}
        isRequired
        validate={fieldRule(shape.title, 'Bitte geben Sie einen Titel an (3 bis 120 Zeichen).')}
      />
      <TextField
        label="Text"
        placeholder="z. B. Admins laden das Logo unter Einstellungen › Auftritt hoch."
        multiline
        rows={5}
        value={body}
        onChange={setBody}
        isRequired
        validate={fieldRule(shape.body, 'Bitte schreiben Sie den Text (10 bis 3000 Zeichen).')}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <DateField
          label="Veröffentlichen am"
          value={at.day}
          onChange={(day) => day && setAt({ ...at, day })}
        />
        <SelectField
          label="Uhrzeit"
          placeholder="Stunde wählen"
          options={HOURS}
          value={at.hour}
          onChange={(hour) => setAt({ ...at, hour })}
        />
      </div>
    </FormDialog>
  );
}

/**
 * What is new in Kanos (after Corly's announcements), written by the Kanos team: the firms see
 * each from its publication, newest first; each change stands in the platform's audit log.
 */
export function AnnouncementsPage() {
  const announcements = useQuery(announcementsQuery);
  const remove = useDeleteAnnouncement();
  const [editing, setEditing] = useState<Announcement | 'new' | null>(null);
  const [deleting, setDeleting] = useState<Announcement | null>(null);
  // When the page opened: what is published by then, the rest planned.
  const [now] = useState(() => Date.now());

  return (
    <Card
      id="platform-announcements"
      title="Neuigkeiten"
      description="Was sich in Kanos ändert, für die Menschen der Kanzleien. Ungelesenes zeigt die Kanzlei-App mit einem Punkt an der Hilfe."
      action={<AddButton label="Neuigkeit schreiben" onPress={() => setEditing('new')} />}
    >
      <QueryContent query={announcements}>
        {(list) =>
          list.length === 0 ? (
            <p className="text-sm text-muted">Noch keine Neuigkeiten.</p>
          ) : (
            <ul className="divide-y divide-hairline border-y border-hairline">
              {list.map((entry) => {
                const published = Date.parse(entry.publishedAt) <= now;
                return (
                  <li key={entry.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3">
                    <div className="grid min-w-0 flex-1 basis-64 gap-0.5">
                      <p className="font-medium">{entry.title}</p>
                      <p className="text-sm text-muted">
                        {published ? 'Veröffentlicht am' : 'Erscheint am'}{' '}
                        {formatDateTime(entry.publishedAt)}
                      </p>
                    </div>
                    <StatusBadge tone={published ? 'success' : 'neutral'}>
                      {published ? 'Veröffentlicht' : 'Geplant'}
                    </StatusBadge>
                    <Button variant="quiet" size="sm" onPress={() => setEditing(entry)}>
                      Ändern<span className="sr-only">: {entry.title}</span>
                    </Button>
                    <Button variant="quiet" size="sm" onPress={() => setDeleting(entry)}>
                      Löschen<span className="sr-only">: {entry.title}</span>
                    </Button>
                  </li>
                );
              })}
            </ul>
          )
        }
      </QueryContent>
      {editing && (
        <AnnouncementDialog
          entry={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      {deleting && (
        <DeleteEntryDialog
          title="Neuigkeit löschen?"
          description={`„${deleting.title}“ verschwindet für alle Kanzleien.`}
          remove={remove}
          id={deleting.id}
          onClose={() => setDeleting(null)}
        />
      )}
    </Card>
  );
}
